import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { LivechatService, ChatSession, ChatMessage } from '../../api/services/livechat.service';
import { AuthService } from '../../api/services/auth.service';
import { HeaderNavComponent } from 'src/app/ui/header-nav/header-nav.component';
import { FooterComponent } from 'src/app/ui/footer/footer.component';
import { Subscription } from 'rxjs';

/**
 * Unified chat surface. Behaves in two modes based on JWT + query params:
 *  - Staff (user_type=BAR_STAFF): lists incoming customer sessions and lets
 *    the staff member reply.
 *  - Customer: lists their own sessions. If a `?bar=<id>` query param is
 *    supplied (e.g. from the QR flow), a new session with that bar is
 *    started automatically.
 */
@Component({
  selector: 'app-livechat',
  standalone: true,
  imports: [
    CommonModule, MatCardModule, MatButtonModule, MatIconModule,
    MatInputModule, MatFormFieldModule, MatListModule,
    MatSnackBarModule, MatProgressSpinnerModule, FormsModule,
    HeaderNavComponent, FooterComponent
  ],
  templateUrl: './livechat.component.html'
})
export class LivechatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messageContainer') private messageContainer!: ElementRef;

  isLoading = false;
  sessions: ChatSession[] = [];
  selectedSession: ChatSession | null = null;
  messages: ChatMessage[] = [];
  newMessage = '';

  myEmail: string | null = null;
  myUserId: number | null = null;
  isStaff = false;

  private msgSub: Subscription | null = null;
  private pollTimer: any = null;

  constructor(
    private livechatService: LivechatService,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    const claims = await this.auth.getClaims();
    this.myEmail = claims?.sub ?? null;
    const rawId = claims?.user_id;
    this.myUserId = rawId != null ? Number(rawId) : null;
    this.isStaff = (claims?.user_type === 'BAR_STAFF');

    if (!this.myEmail || !this.myUserId) {
      this.snackBar.open('You must be signed in to use chat', 'Close', { duration: 3000 });
      this.router.navigate(['/login']);
      return;
    }

    // Wire up WebSocket + real-time message subscription
    try {
      await this.livechatService.connect();
    } catch (e) {
      // still render the session list even if WS is temporarily down
    }
    this.msgSub = this.livechatService.messages$.subscribe(msg => {
      // Only append if it belongs to the currently open session
      if (this.selectedSession && msg.chatSessionId === String(this.selectedSession.id)) {
        this.messages.push(msg);
      }
      // Bump lastMessage on the session list card
      const s = this.sessions.find(x => String(x.id) === msg.chatSessionId);
      if (s) s.lastMessage = msg.chatMessage;
    });

    // If we arrived with ?bar=<id>, that's the QR / "chat with bar" flow.
    const barId = this.route.snapshot.queryParamMap.get('bar');
    if (barId && !this.isStaff) {
      this.startChatWithBar(barId);
    } else {
      this.loadSessions();
    }

    // Poll every 4s as a WebSocket-independent safety net. The MVC gateway
    // does not proxy WebSockets, so REST polling is what actually delivers
    // new messages to open chats in this deployment.
    this.pollTimer = setInterval(() => this.pollTick(), 4000);
  }

  ngOnDestroy(): void {
    this.livechatService.disconnect();
    this.msgSub?.unsubscribe();
    if (this.pollTimer) clearInterval(this.pollTimer);
  }

  private pollTick(): void {
    // Refresh the current conversation's messages
    if (this.selectedSession) {
      this.livechatService.getMessagesBySession(this.selectedSession.id).subscribe({
        next: (msgs) => {
          if ((msgs?.length ?? 0) !== this.messages.length) {
            this.messages = msgs ?? [];
          }
        },
        error: () => { /* keep the last-known messages on transient failures */ }
      });
    }
    // Refresh the session list so new incoming chats/last-message appear
    const source = this.isStaff
      ? this.livechatService.incomingSessions()
      : this.livechatService.mySessions();
    source.subscribe({
      next: (sessions) => { if (sessions) this.sessions = sessions; },
      error: () => { /* ignore */ }
    });
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  loadSessions(): void {
    this.isLoading = true;
    const source = this.isStaff
      ? this.livechatService.incomingSessions()
      : this.livechatService.mySessions();
    source.subscribe({
      next: (sessions) => {
        this.sessions = sessions ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open('Failed to load conversations', 'Close', { duration: 3000 });
      }
    });
  }

  selectSession(session: ChatSession): void {
    this.selectedSession = session;
    this.messages = [];
    this.livechatService.getMessagesBySession(session.id).subscribe({
      next: (msgs) => { this.messages = msgs ?? []; },
      error: () => this.snackBar.open('Failed to load messages', 'Close', { duration: 3000 })
    });
  }

  /** Customer-side: kicked off from ?bar=<id> in the URL. */
  startChatWithBar(barId: string): void {
    this.isLoading = true;
    this.livechatService.startChatWithBar(barId).subscribe({
      next: (session) => {
        this.isLoading = false;
        const existing = this.sessions.find(s => s.id === session.id);
        if (!existing) this.sessions.unshift(session);
        this.selectSession(session);
        this.snackBar.open('Chat started with the bar', 'Close', { duration: 2500 });
      },
      error: (err) => {
        this.isLoading = false;
        const msg = err?.error?.message || 'Could not start chat — no staff on duty?';
        this.snackBar.open(msg, 'Close', { duration: 4000 });
        this.loadSessions();
      }
    });
  }

  sendMessage(): void {
    const text = this.newMessage.trim();
    if (!text || !this.selectedSession || !this.myEmail) return;

    const optimistic: ChatMessage = {
      chatSessionId: String(this.selectedSession.id),
      chatMessage: text,
      origin: 'web',
      originEmail: this.myEmail,
      dateOfMessage: new Date().toISOString(),
    };
    // Optimistic append — reconciled by the next poll if the send fails
    this.messages.push(optimistic);
    this.newMessage = '';

    const result = this.livechatService.sendMessage(this.selectedSession.id, text);
    if (result && (result as any).subscribe) {
      (result as any).subscribe({
        next: () => { /* server ack; the next poll picks up the persisted row */ },
        error: () => {
          this.snackBar.open('Message failed to send', 'Close', { duration: 3000 });
        }
      });
    }
  }

  otherParty(session: ChatSession): string {
    return this.myEmail && session.emailCustomer === this.myEmail
      ? session.emailProvider
      : session.emailCustomer;
  }

  isMine(msg: ChatMessage): boolean {
    return !!this.myEmail && msg.originEmail === this.myEmail;
  }

  private scrollToBottom(): void {
    try {
      if (this.messageContainer) {
        this.messageContainer.nativeElement.scrollTop =
          this.messageContainer.nativeElement.scrollHeight;
      }
    } catch {}
  }

  onBack(): void {
    this.router.navigate(['/']);
  }
}
